import React, { useState } from 'react';
import { C, F } from '../constants';
import { useTheme } from '../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  HomeIcon, FileTextIcon, BarChartIcon, TargetIcon, UsersIcon, SettingsIcon, LogOutIcon, 
  UploadIcon, CreditCardIcon, BellIcon, TrendingUpIcon, ClockIcon,
  CheckCircleIcon, AlertCircleIcon, ZapIcon, DownloadIcon, EyeIcon, PlusIcon, SearchIcon,
  MoonIcon, SunIcon, MenuIcon, XIcon, ArrowUpIcon, CoinsIcon
} from './BrandIcons';
import { ChevronRight } from 'lucide-react';
import { PaymentCheckout } from './PaymentCheckout';

// Sidebar Navigation Component
const Sidebar = ({ activeTab, setActiveTab, user, isCollapsed, setIsCollapsed }) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  const navItems = [
    { id: 'overview', icon: <HomeIcon size={20} />, label: 'Overview' },
    { id: 'decks', icon: <FileTextIcon size={20} />, label: 'My Decks' },
    { id: 'intelligence', icon: <BarChartIcon size={20} />, label: 'Intelligence' },
    { id: 'artefacts', icon: <TargetIcon size={20} />, label: 'Artefacts' },
    { id: 'strategy', icon: <UsersIcon size={20} />, label: 'Strategy Calls' },
  ];

  const bottomItems = [
    { id: 'settings', icon: <SettingsIcon size={20} />, label: 'Settings' },
  ];

  return (
    <motion.div
      animate={{ width: isCollapsed ? '72px' : '260px' }}
      transition={{ duration: 0.3 }}
      style={{
        background: isDark ? '#0A0B0D' : '#FAFAFA',
        borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 100,
        overflow: 'hidden'
      }}
    >
      {/* Logo */}
      <div style={{
        padding: '20px',
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ 
          fontFamily: F, 
          fontSize: isCollapsed ? '20px' : '24px', 
          fontWeight: 700,
          whiteSpace: 'nowrap'
        }}>
          <span style={{ color: C.secondary }}>Dec</span>
          {!isCollapsed && <span style={{ color: isDark ? C.white : C.black }}>X AI</span>}
        </div>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
            padding: '4px',
            display: isCollapsed ? 'none' : 'block'
          }}
        >
          <MenuIcon size={18} />
        </motion.button>
      </div>

      {/* User Info */}
      {!isCollapsed && (
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: `linear-gradient(135deg, ${C.secondary}, ${C.primary})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 700,
              fontSize: '16px'
            }}>
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ 
                fontSize: '14px', 
                fontWeight: 700, 
                color: isDark ? C.white : C.black,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {user?.name || 'User'}
              </div>
              <div style={{ 
                fontSize: '12px', 
                color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {user?.email || 'user@email.com'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
        {navItems.map((item) => (
          <motion.button
            key={item.id}
            whileHover={{ x: 4 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setActiveTab(item.id)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: isCollapsed ? '12px' : '12px 16px',
              borderRadius: '10px',
              border: 'none',
              background: activeTab === item.id 
                ? (isDark ? `${C.secondary}20` : `${C.secondary}15`)
                : 'transparent',
              color: activeTab === item.id 
                ? C.secondary 
                : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
              cursor: 'pointer',
              marginBottom: '4px',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              transition: 'all 0.2s ease'
            }}
          >
            {item.icon}
            {!isCollapsed && (
              <span style={{ fontSize: '14px', fontWeight: activeTab === item.id ? 700 : 500 }}>
                {item.label}
              </span>
            )}
          </motion.button>
        ))}
      </div>

      {/* Bottom Actions */}
      <div style={{ 
        padding: '16px 12px', 
        borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` 
      }}>
        {/* Theme Toggle */}
        <motion.button
          whileHover={{ x: 4 }}
          whileTap={{ scale: 0.98 }}
          onClick={toggleTheme}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: isCollapsed ? '12px' : '12px 16px',
            borderRadius: '10px',
            border: 'none',
            background: 'transparent',
            color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
            cursor: 'pointer',
            marginBottom: '4px',
            justifyContent: isCollapsed ? 'center' : 'flex-start'
          }}
        >
          {isDark ? <SunIcon size={20} /> : <MoonIcon size={20} />}
          {!isCollapsed && <span style={{ fontSize: '14px' }}>{isDark ? 'Light Mode' : 'Dark Mode'}</span>}
        </motion.button>

        {bottomItems.map((item) => (
          <motion.button
            key={item.id}
            whileHover={{ x: 4 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setActiveTab(item.id)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: isCollapsed ? '12px' : '12px 16px',
              borderRadius: '10px',
              border: 'none',
              background: activeTab === item.id ? `${C.secondary}20` : 'transparent',
              color: activeTab === item.id ? C.secondary : (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
              cursor: 'pointer',
              marginBottom: '4px',
              justifyContent: isCollapsed ? 'center' : 'flex-start'
            }}
          >
            {item.icon}
            {!isCollapsed && <span style={{ fontSize: '14px' }}>{item.label}</span>}
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
};

// Stats Card Component
const StatCard = ({ icon, label, value, change, color = C.secondary }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <motion.div
      whileHover={{ y: -4, boxShadow: `0 8px 30px ${color}20` }}
      style={{
        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.9)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        borderRadius: '16px',
        padding: '24px',
        cursor: 'pointer'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          background: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color
        }}>
          {icon}
        </div>
        {change && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px',
            borderRadius: '100px',
            background: change > 0 ? 'rgba(0,212,170,0.1)' : 'rgba(255,107,107,0.1)',
            color: change > 0 ? '#00D4AA' : '#FF6B6B',
            fontSize: '12px',
            fontWeight: 700
          }}>
            <TrendingUpIcon size={14} />
            {change > 0 ? '+' : ''}{change}%
          </div>
        )}
      </div>
      <div style={{ 
        fontSize: '32px', 
        fontWeight: 700, 
        fontFamily: F, 
        marginBottom: '4px',
        color: isDark ? C.white : C.black
      }}>
        {value}
      </div>
      <div style={{ fontSize: '14px', color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
        {label}
      </div>
    </motion.div>
  );
};

// Deck Card Component
const DeckCard = ({ deck, onView }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const getScoreColor = (score) => {
    if (score >= 70) return '#00D4AA';
    if (score >= 50) return '#FFB800';
    return '#FF6B6B';
  };

  return (
    <motion.div
      whileHover={{ y: -4, borderColor: C.secondary }}
      style={{
        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.9)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        borderRadius: '16px',
        padding: '20px',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
      onClick={onView}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ 
            fontSize: '16px', 
            fontWeight: 700, 
            marginBottom: '4px',
            color: isDark ? C.white : C.black
          }}>
            {deck.name}
          </div>
          <div style={{ fontSize: '12px', color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
            {deck.slides} slides • {deck.type}
          </div>
        </div>
        <div style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: `${getScoreColor(deck.score)}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column'
        }}>
          <span style={{ fontSize: '18px', fontWeight: 700, color: getScoreColor(deck.score) }}>
            {deck.score}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <span style={{
          padding: '4px 10px',
          borderRadius: '100px',
          background: deck.status === 'Completed' ? 'rgba(0,212,170,0.1)' : 'rgba(255,184,0,0.1)',
          color: deck.status === 'Completed' ? '#00D4AA' : '#FFB800',
          fontSize: '11px',
          fontWeight: 700
        }}>
          {deck.status}
        </span>
        <span style={{
          padding: '4px 10px',
          borderRadius: '100px',
          background: `${C.secondary}15`,
          color: C.secondary,
          fontSize: '11px',
          fontWeight: 700
        }}>
          {deck.plan}
        </span>
      </div>

      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        paddingTop: '12px',
        borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
          <ClockIcon size={14} />
          {deck.date}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            style={{
              padding: '6px',
              borderRadius: '8px',
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              border: 'none',
              cursor: 'pointer',
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'
            }}
          >
            <EyeIcon size={16} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            style={{
              padding: '6px',
              borderRadius: '8px',
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              border: 'none',
              cursor: 'pointer',
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'
            }}
          >
            <DownloadIcon size={16} />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

// Quick Action Card
const QuickActionCard = ({ icon, title, description, color, onClick }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <motion.div
      whileHover={{ y: -4, borderColor: color, boxShadow: `0 8px 30px ${color}20` }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      style={{
        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.9)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        borderRadius: '16px',
        padding: '24px',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
    >
      <div style={{
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        background: `${color}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color,
        marginBottom: '16px'
      }}>
        {icon}
      </div>
      <div style={{ 
        fontSize: '16px', 
        fontWeight: 700, 
        marginBottom: '8px',
        color: isDark ? C.white : C.black
      }}>
        {title}
      </div>
      <div style={{ fontSize: '13px', color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', lineHeight: 1.5 }}>
        {description}
      </div>
    </motion.div>
  );
};

// Overview Tab Content
const OverviewTab = ({ user, decks }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div>
      {/* Welcome Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: `linear-gradient(135deg, ${C.primary}20 0%, ${C.secondary}20 100%)`,
          border: `1px solid ${C.secondary}30`,
          borderRadius: '20px',
          padding: '32px',
          marginBottom: '32px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{ 
            fontFamily: F, 
            fontSize: '28px', 
            fontWeight: 700, 
            marginBottom: '8px',
            color: isDark ? C.white : C.black
          }}>
            Welcome back, {user?.name?.split(' ')[0] || 'there'}! 👋
          </h1>
          <p style={{ fontSize: '16px', color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', marginBottom: '24px' }}>
            Your deck intelligence dashboard is ready. Let's transform your next presentation.
          </p>
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: '0 8px 30px rgba(0,130,251,0.5)' }}
            whileTap={{ scale: 0.95 }}
            style={{
              background: C.grad,
              color: C.white,
              padding: '12px 24px',
              borderRadius: '100px',
              border: 'none',
              fontWeight: 700,
              fontSize: '14px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: F
            }}
          >
            <UploadIcon size={18} /> Upload New Deck
          </motion.button>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '20px',
        marginBottom: '32px'
      }}>
        <StatCard icon={<FileTextIcon size={24} />} label="Decks Analyzed" value="3" change={50} color={C.secondary} />
        <StatCard icon={<BarChartIcon size={24} />} label="Avg. DecX Score" value="72" change={8} color="#00D4AA" />
        <StatCard icon={<CreditCardIcon size={24} />} label="Credits Available" value="47" color="#FFB800" />
        <StatCard icon={<ZapIcon size={24} />} label="Issues Fixed" value="12" change={25} color="#8B5CF6" />
      </div>

      {/* Quick Actions */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ 
          fontSize: '18px', 
          fontWeight: 700, 
          marginBottom: '16px',
          color: isDark ? C.white : C.black
        }}>
          Quick Actions
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
          <QuickActionCard 
            icon={<UploadIcon size={24} />}
            title="Upload & Score"
            description="Get your free DecX Score in 24 hours"
            color={C.secondary}
          />
          <QuickActionCard 
            icon={<TargetIcon size={24} />}
            title="View Artefacts"
            description="Access your Pitch Canvas & templates"
            color="#00D4AA"
          />
          <QuickActionCard 
            icon={<UsersIcon size={24} />}
            title="Book Strategy Call"
            description="30-min session with a deck expert"
            color="#8B5CF6"
          />
        </div>
      </div>

      {/* Recent Decks */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ 
            fontSize: '18px', 
            fontWeight: 700,
            color: isDark ? C.white : C.black
          }}>
            Recent Decks
          </h2>
          <motion.button
            whileHover={{ color: C.secondary }}
            style={{
              background: 'transparent',
              border: 'none',
              color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            View All <ChevronRight size={16} />
          </motion.button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
          {decks.map((deck, i) => (
            <DeckCard key={i} deck={deck} onView={() => {}} />
          ))}
        </div>
      </div>
    </div>
  );
};

// My Decks Tab Content
const DecksTab = ({ decks }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ 
          fontFamily: F, 
          fontSize: '24px', 
          fontWeight: 700,
          color: isDark ? C.white : C.black
        }}>
          My Decks
        </h1>
        <motion.button
          whileHover={{ scale: 1.05, boxShadow: '0 8px 30px rgba(0,130,251,0.5)' }}
          whileTap={{ scale: 0.95 }}
          style={{
            background: C.grad,
            color: C.white,
            padding: '10px 20px',
            borderRadius: '100px',
            border: 'none',
            fontWeight: 700,
            fontSize: '14px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontFamily: F
          }}
        >
          <PlusIcon size={18} /> Upload Deck
        </motion.button>
      </div>

      {/* Search */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
        borderRadius: '12px',
        marginBottom: '24px'
      }}>
        <SearchIcon size={20} />
        <input
          type="text"
          placeholder="Search decks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            fontSize: '14px',
            color: isDark ? C.white : C.black
          }}
        />
      </div>

      {/* Decks Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
        {decks.map((deck, i) => (
          <DeckCard key={i} deck={deck} onView={() => {}} />
        ))}
        
        {/* Empty Upload Card */}
        <motion.div
          whileHover={{ borderColor: C.secondary, background: `${C.secondary}05` }}
          style={{
            border: `2px dashed ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'}`,
            borderRadius: '16px',
            padding: '40px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            minHeight: '200px'
          }}
        >
          <UploadIcon size={32} style={{ marginBottom: '12px', opacity: 0.4 }} />
          <div style={{ fontSize: '14px', fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
            Upload New Deck
          </div>
          <div style={{ fontSize: '12px', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', marginTop: '4px' }}>
            PDF or PPTX
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Intelligence Tab Content
const IntelligenceTab = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const modules = [
    { icon: <BarChartIcon size={24} />, title: 'DecX Score Analysis', desc: 'Deep dive into your 5-dimension breakdown', status: 'Available', color: C.secondary },
    { icon: <SearchIcon size={24} />, title: 'Unexplored Intelligence', desc: 'Discover gaps you didn\'t know existed', status: 'Available', color: '#00D4AA' },
    { icon: <UsersIcon size={24} />, title: 'Audience Insights', desc: 'Understand who you\'re pitching', status: '2 Credits', color: '#8B5CF6' },
    { icon: <TrendingUpIcon size={24} />, title: 'Industry Trends', desc: 'See what\'s working in your sector', status: '3 Credits', color: '#FFB800' },
  ];

  return (
    <div>
      <h1 style={{ 
        fontFamily: F, 
        fontSize: '24px', 
        fontWeight: 700, 
        marginBottom: '8px',
        color: isDark ? C.white : C.black
      }}>
        Intelligence Suite
      </h1>
      <p style={{ fontSize: '14px', color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', marginBottom: '32px' }}>
        Deep insights to transform your deck performance
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
        {modules.map((module, i) => (
          <motion.div
            key={i}
            whileHover={{ y: -4, borderColor: module.color }}
            style={{
              background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.9)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              borderRadius: '16px',
              padding: '24px',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: `${module.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: module.color
              }}>
                {module.icon}
              </div>
              <span style={{
                padding: '4px 10px',
                borderRadius: '100px',
                background: module.status === 'Available' ? 'rgba(0,212,170,0.1)' : `${C.secondary}15`,
                color: module.status === 'Available' ? '#00D4AA' : C.secondary,
                fontSize: '11px',
                fontWeight: 700
              }}>
                {module.status}
              </span>
            </div>
            <div style={{ 
              fontSize: '16px', 
              fontWeight: 700, 
              marginBottom: '8px',
              color: isDark ? C.white : C.black
            }}>
              {module.title}
            </div>
            <div style={{ fontSize: '13px', color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
              {module.desc}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// Main Dashboard Component
export const UserDashboard = ({ user, onLogout }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Payment modal states
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState(null);
  const [selectedCreditPack, setSelectedCreditPack] = useState(null);
  const [currency, setCurrency] = useState('INR');
  const [billingCycle, setBillingCycle] = useState('monthly');

  // Upgrade plans with USD and yearly pricing
  const upgradePlans = [
    { id: 'starter', name: 'Starter', price_monthly_inr: 999, price_monthly_usd: 12, price_yearly_inr: 9990, price_yearly_usd: 120, credits: 5 },
    { id: 'growth', name: 'Growth', price_monthly_inr: 2499, price_monthly_usd: 30, price_yearly_inr: 24990, price_yearly_usd: 300, credits: 15 },
    { id: 'scale', name: 'Scale', price_monthly_inr: 4999, price_monthly_usd: 60, price_yearly_inr: 49990, price_yearly_usd: 600, credits: 50 },
  ];

  // Credit packs with USD pricing
  const creditPacks = [
    { id: 'pack_5', credits: 5, price_inr: 499, price_usd: 6 },
    { id: 'pack_10', credits: 10, price_inr: 899, price_usd: 11 },
    { id: 'pack_25', credits: 25, price_inr: 1999, price_usd: 24 },
    { id: 'pack_50', credits: 50, price_inr: 3499, price_usd: 42 },
  ];

  // Sample deck data
  const decks = [
    { name: 'TechVenture_Pitch.pdf', slides: 14, type: 'Pitch Deck', score: 72, status: 'Completed', plan: 'Growth', date: '2 hours ago' },
    { name: 'Q4_Sales_Deck.pptx', slides: 18, type: 'Sales Deck', score: 85, status: 'Completed', plan: 'Starter', date: 'Yesterday' },
    { name: 'Product_Roadmap.pdf', slides: 12, type: 'Product Deck', score: 58, status: 'In Progress', plan: 'Free', date: '3 days ago' },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab user={user} decks={decks} />;
      case 'decks':
        return <DecksTab decks={decks} />;
      case 'intelligence':
        return <IntelligenceTab />;
      case 'artefacts':
        return (
          <div>
            <h1 style={{ fontFamily: F, fontSize: '24px', fontWeight: 700, marginBottom: '16px', color: isDark ? C.white : C.black }}>
              Artefacts
            </h1>
            <p style={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
              Your ready-to-use closing materials will appear here.
            </p>
          </div>
        );
      case 'strategy':
        return (
          <div>
            <h1 style={{ fontFamily: F, fontSize: '24px', fontWeight: 700, marginBottom: '16px', color: isDark ? C.white : C.black }}>
              Strategy Calls
            </h1>
            <p style={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
              Book 1:1 sessions with deck experts.
            </p>
          </div>
        );
      case 'settings':
        return (
          <div>
            <h1 style={{ fontFamily: F, fontSize: '24px', fontWeight: 700, marginBottom: '16px', color: isDark ? C.white : C.black }}>
              Settings
            </h1>
            <p style={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
              Manage your account preferences.
            </p>
          </div>
        );
      default:
        return <OverviewTab user={user} decks={decks} />;
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: isDark ? '#0D1117' : '#F5F5F7',
      color: isDark ? C.white : C.black
    }}>
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        user={user}
        isCollapsed={sidebarCollapsed}
        setIsCollapsed={setSidebarCollapsed}
      />
      
      {/* Main Content */}
      <motion.div
        animate={{ marginLeft: sidebarCollapsed ? '72px' : '260px' }}
        transition={{ duration: 0.3 }}
        style={{ padding: '32px', minHeight: '100vh' }}
      >
        {/* Top Bar */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '32px'
        }}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            style={{
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              border: 'none',
              borderRadius: '10px',
              padding: '10px',
              cursor: 'pointer',
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'
            }}
          >
            <MenuIcon size={20} />
          </motion.button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Upgrade Plan Button */}
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: '0 4px 20px rgba(0,130,251,0.3)' }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowUpgradeModal(true)}
              style={{
                background: C.grad,
                border: 'none',
                borderRadius: '100px',
                padding: '8px 16px',
                cursor: 'pointer',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                fontWeight: 700,
                fontFamily: F
              }}
              data-testid="upgrade-plan-btn"
            >
              <ArrowUpIcon size={16} />
              Upgrade
            </motion.button>

            {/* Add Credits Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCreditsModal(true)}
              style={{
                background: isDark ? 'rgba(255,184,0,0.15)' : 'rgba(255,184,0,0.1)',
                border: '1px solid rgba(255,184,0,0.3)',
                borderRadius: '100px',
                padding: '8px 16px',
                cursor: 'pointer',
                color: '#FFB800',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                fontWeight: 700,
                fontFamily: F
              }}
              data-testid="add-credits-btn"
            >
              <CoinsIcon size={16} />
              Add Credits
            </motion.button>

            {/* Notifications */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              style={{
                background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                border: 'none',
                borderRadius: '10px',
                padding: '10px',
                cursor: 'pointer',
                color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                position: 'relative'
              }}
            >
              <BellIcon size={20} />
              <div style={{
                position: 'absolute',
                top: '6px',
                right: '6px',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#FF6B6B'
              }} />
            </motion.button>

            {/* Credits Display */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              borderRadius: '100px'
            }}>
              <ZapIcon size={16} />
              <span style={{ fontSize: '14px', fontWeight: 700 }}>{user?.credits || 47} Credits</span>
            </div>

            {/* Logout */}
            <motion.button
              whileHover={{ scale: 1.05, background: 'rgba(255,107,107,0.1)' }}
              whileTap={{ scale: 0.95 }}
              onClick={onLogout}
              style={{
                background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                border: 'none',
                borderRadius: '10px',
                padding: '10px',
                cursor: 'pointer',
                color: '#FF6B6B'
              }}
            >
              <LogOutIcon size={20} />
            </motion.button>
          </div>
        </div>

        {/* Page Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Upgrade Plan Modal */}
      {showUpgradeModal && (
        <PaymentCheckout
          isOpen={showUpgradeModal}
          onClose={() => {
            setShowUpgradeModal(false);
            setSelectedUpgradePlan(null);
          }}
          type="subscription"
          plan={selectedUpgradePlan}
          onPlanSelect={setSelectedUpgradePlan}
          plans={upgradePlans}
          user={user}
          currency={currency}
          onCurrencyChange={setCurrency}
          billingCycle={billingCycle}
          onBillingCycleChange={setBillingCycle}
        />
      )}

      {/* Add Credits Modal */}
      {showCreditsModal && (
        <PaymentCheckout
          isOpen={showCreditsModal}
          onClose={() => {
            setShowCreditsModal(false);
            setSelectedCreditPack(null);
          }}
          type="credits"
          creditPack={selectedCreditPack}
          onCreditPackSelect={setSelectedCreditPack}
          creditPacks={creditPacks}
          user={user}
          currency={currency}
          onCurrencyChange={setCurrency}
        />
      )}
    </div>
  );
};

export default UserDashboard;
